import { useMemo, useState } from "react"
import { CalendarDays, CheckCircle2, Users, XCircle } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { BarChart } from "../components/charts/Charts"
import { useLocale } from "../components/LocaleProvider"
import { useArchivedRecords, useClients, useDashboardRecords } from "../lib/queries"
import { getUserColor } from "../lib/utils"
import type { TranslationKey } from "../i18n/translations"
import type { DashboardRecord } from "../types"

type PeriodKey = "week" | "month" | "quarter"

const PERIODS: { key: PeriodKey; days: number; label: TranslationKey }[] = [
  { key: "week", days: 7, label: "statistic_period_week" },
  { key: "month", days: 30, label: "statistic_period_month" },
  { key: "quarter", days: 90, label: "statistic_period_quarter" },
]

const CANCELLED_STATUSES = new Set(["Absage", "Denied", "No relevant"])

function startOfDay(timestamp: number): number {
  const d = new Date(timestamp)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function endOfDay(timestamp: number): number {
  const d = new Date(timestamp)
  d.setHours(23, 59, 59, 999)
  return d.getTime()
}

function formatDay(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

function formatTermin(terminDate: string): string {
  const date = new Date(terminDate)
  if (isNaN(date.getTime())) return "—"
  return `${date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })} ${date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
}

interface ClientStat {
  client: string
  total: number
  completed: number
  cancelled: number
  open: number
  lastTermin: string
  lastTimestamp: number
}

function Statistic() {
  const { t } = useLocale()
  const [period, setPeriod] = useState<PeriodKey>("month")

  const { data: activeData, isLoading: loadingActive } = useDashboardRecords()
  const { data: archivedData, isLoading: loadingArchived } = useArchivedRecords()
  const { data: clientsData, isLoading: loadingClients } = useClients()

  const disabledClientNames = useMemo(() => {
    const set = new Set<string>()
    if (clientsData) {
      for (const client of clientsData) {
        if (client.enabled === false) {
          if (client.fullName) set.add(client.fullName)
        }
      }
    }
    return set
  }, [clientsData])

  const records = useMemo<DashboardRecord[]>(
    () =>
      [...(activeData ?? []), ...(archivedData ?? [])].filter(
        (record) => !disabledClientNames.has(record.client),
      ),
    [activeData, archivedData, disabledClientNames],
  )

  const days = PERIODS.find((p) => p.key === period)?.days ?? 30

  const stats = useMemo(() => {
    const now = Date.now()
    const end = endOfDay(now)
    const start = startOfDay(now - (days - 1) * 86400000)
    const byClient = new Map<string, ClientStat>()

    let total = 0
    let completed = 0
    let cancelled = 0

    for (const record of records) {
      if (!record.terminDate) continue
      const timestamp = new Date(record.terminDate).getTime()
      if (!Number.isFinite(timestamp) || timestamp < start || timestamp > end) continue

      const client = record.client || "—"
      let clientStat = byClient.get(client)
      if (!clientStat) {
        clientStat = {
          client,
          total: 0,
          completed: 0,
          cancelled: 0,
          open: 0,
          lastTermin: "",
          lastTimestamp: 0,
        }
        byClient.set(client, clientStat)
      }

      clientStat.total += 1
      total += 1

      if (record.status === "Completed") {
        clientStat.completed += 1
        completed += 1
      } else if (CANCELLED_STATUSES.has(record.status)) {
        clientStat.cancelled += 1
        cancelled += 1
      } else {
        clientStat.open += 1
      }

      if (timestamp > clientStat.lastTimestamp) {
        clientStat.lastTimestamp = timestamp
        clientStat.lastTermin = record.terminDate
      }
    }

    const clients = Array.from(byClient.values()).sort(
      (a, b) => b.total - a.total || a.client.localeCompare(b.client),
    )

    return { start, end, total, completed, cancelled, clients }
  }, [records, days])

  const chartData = useMemo(
    () => stats.clients.slice(0, 10).map((c) => ({ label: c.client, value: c.total })),
    [stats.clients],
  )

  const isLoading = loadingActive || loadingArchived || loadingClients

  const cards = [
    {
      label: t("statistic_total_termins"),
      value: stats.total,
      icon: CalendarDays,
      color: "text-primary",
    },
    {
      label: t("statistic_clients"),
      value: stats.clients.length,
      icon: Users,
      color: "text-blue-500",
    },
    {
      label: t("statistic_completed"),
      value: stats.completed,
      icon: CheckCircle2,
      color: "text-emerald-500",
    },
    {
      label: t("statistic_cancelled"),
      value: stats.cancelled,
      icon: XCircle,
      color: "text-red-500",
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t("statistic_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("statistic_subtitle")}</p>
        </div>
        <div className="space-y-1 text-right">
          <div className="inline-flex items-center gap-1 rounded-md border border-input bg-background p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`h-8 rounded px-3 text-sm font-medium transition-colors ${
                  period === p.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {t(p.label)}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("statistic_range", formatDay(stats.start), formatDay(stats.end))}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          {t("loading")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {cards.map((card) => (
              <div key={card.label} className="rounded-lg border bg-card p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{card.label}</p>
                  <card.icon className={`h-4 w-4 ${card.color}`} />
                </div>
                <p className="mt-2 text-2xl font-semibold">{card.value}</p>
              </div>
            ))}
          </div>

          {stats.clients.length === 0 ? (
            <div className="rounded-lg border bg-card py-12 text-center text-muted-foreground">
              {t("statistic_empty")}
            </div>
          ) : (
            <>
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-2 text-sm font-medium">{t("statistic_top_clients")}</div>
                <BarChart data={chartData} formatValue={(v) => String(Math.round(v))} />
              </div>

              <div className="rounded-lg border bg-card">
                <div className="border-b px-4 py-3 text-sm font-medium">
                  {t("statistic_by_client")}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("th_client")}</TableHead>
                      <TableHead className="text-right">{t("statistic_termins")}</TableHead>
                      <TableHead className="text-right">{t("statistic_completed")}</TableHead>
                      <TableHead className="text-right">{t("statistic_cancelled")}</TableHead>
                      <TableHead className="text-right">{t("statistic_open")}</TableHead>
                      <TableHead className="text-right">{t("statistic_share")}</TableHead>
                      <TableHead>{t("statistic_last_termin")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.clients.map((client) => (
                      <TableRow key={client.client}>
                        <TableCell>
                          <span className="flex items-center gap-2 whitespace-nowrap font-medium">
                            <span
                              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: getUserColor(client.client) }}
                            />
                            {client.client}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{client.total}</TableCell>
                        <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
                          {client.completed}
                        </TableCell>
                        <TableCell className="text-right text-red-600 dark:text-red-400">
                          {client.cancelled}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {client.open}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {stats.total > 0 ? Math.round((client.total / stats.total) * 100) : 0}%
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {client.lastTermin ? formatTermin(client.lastTermin) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default Statistic
